import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { API_ERROR_CODES } from '@solar/contracts';

import { Alert } from '../../components/Alert';
import { PageSpinner } from '../../components/PageSpinner';
import { authApi } from '../../features/auth/auth.api';
import { ApiError } from '../../lib/api-error';
import { toDisplayMessage } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import { AuthLayout } from './AuthLayout';

type VerificationState =
  | { kind: 'verifying' }
  | { kind: 'missing-token' }
  | { kind: 'verified' }
  | { kind: 'failed'; message: string };

function VerificationRecoveryFooter(): React.JSX.Element {
  return (
    <>
      <p>
        <Link to={paths.resendVerification}>Resend verification email</Link>
      </p>
      <p>
        <Link to={paths.login}>Back to sign in</Link>
      </p>
    </>
  );
}

/**
 * Lands the user after they click the emailed link.
 *
 * The token arrives in the query string and is redeemed once on mount. Tokens
 * are single-use, so the guard against a second attempt matters: React's strict
 * mode runs effects twice in development, and without it the first call would
 * consume the token and the second would report it as already used.
 */
export function VerifyEmailPage(): React.JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [state, setState] = useState<VerificationState>({ kind: 'verifying' });
  const attempted = useRef(false);

  useEffect(() => {
    if (token === null || token === '') {
      setState({ kind: 'missing-token' });
      return;
    }

    if (attempted.current) {
      return;
    }

    attempted.current = true;

    authApi
      .verifyEmail(token)
      .then(() => {
        setState({ kind: 'verified' });
      })
      .catch((caught: unknown) => {
        const message =
          caught instanceof ApiError && caught.code === API_ERROR_CODES.INVALID_VERIFICATION_TOKEN
            ? caught.message
            : toDisplayMessage(caught, 'This verification link is invalid or has expired.');

        setState({ kind: 'failed', message });
      });
  }, [token]);

  if (state.kind === 'verifying') {
    return (
      <AuthLayout
        title="Verifying your email"
        description="Please wait while we activate your Seamontech account."
        heroHeadline="Secure access to your Seamontech account."
        heroLead="Activate your account to begin your solar journey."
      >
        <PageSpinner label="Verifying your email" />
      </AuthLayout>
    );
  }

  if (state.kind === 'verified') {
    return (
      <AuthLayout
        title="Email verified"
        description="Your Seamontech account is ready."
        footer={
          <p>
            <Link to={paths.login}>Sign in</Link>
          </p>
        }
      >
        <Alert tone="success">Your Seamontech account is ready. Sign in to continue.</Alert>
      </AuthLayout>
    );
  }

  if (state.kind === 'missing-token') {
    return (
      <AuthLayout
        title="Verification link incomplete"
        description="Open the verification link from your email, or request a new one."
        footer={<VerificationRecoveryFooter />}
      />
    );
  }

  return (
    <AuthLayout
      title="Verification link unavailable"
      description={state.message}
      footer={<VerificationRecoveryFooter />}
    />
  );
}
